import { useInfiniteQuery, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import type { FeedArticle } from '../types';

interface FeedPage {
  readonly data: FeedArticle[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

interface FeedQueryOptions {
  readonly enabled?: boolean;
}

function sanitizeArticles(articles: FeedArticle[]): FeedArticle[] {
  // Strip articles with no title — backend should never return these but
  // historic rows have leaked through. Mirrors mobile/src/config/api-helpers.ts.
  return articles.filter((a) => a.title && a.title.trim().length > 0);
}

export function useFeedQuery(
  category?: string,
  country?: string,
  subscribedOnly?: boolean,
  options?: FeedQueryOptions,
) {
  return useInfiniteQuery({
    queryKey: queryKeys.feed.list(category, country, subscribedOnly),
    queryFn: async ({ pageParam }) => {
      const params: string[] = ['limit=20'];
      if (category) params.push(`category=${encodeURIComponent(category)}`);
      if (country) params.push(`country=${encodeURIComponent(country)}`);
      if (subscribedOnly) params.push('subscribedOnly=true');
      if (pageParam) params.push(`cursor=${encodeURIComponent(pageParam)}`);
      const result = await apiClient.get<{ data: FeedPage }>(`/feed?${params.join('&')}`);
      return result.data.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    enabled: options?.enabled ?? true,
  });
}

export function useFeedArticles(
  category?: string,
  country?: string,
  subscribedOnly?: boolean,
  options?: FeedQueryOptions,
): FeedArticle[] {
  const { data } = useFeedQuery(category, country, subscribedOnly, options);
  return useMemo(() => {
    if (!data?.pages) return [];
    const all = data.pages.flatMap((p) => p.data);
    const seen = new Set<string>();
    const unique = all.filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)));
    return sanitizeArticles(unique);
  }, [data]);
}

export function useArticleByIdQuery(id: string | null) {
  return useQuery({
    queryKey: queryKeys.feed.article(id ?? ''),
    queryFn: async () => {
      const result = await apiClient.get<{ data: FeedArticle }>(`/feed/articles/${id}`);
      return result.data.data;
    },
    enabled: !!id,
  });
}

interface SavedArticleRecord {
  readonly articleHash: string;
  readonly title: string;
  readonly summary?: string | null;
  readonly imageUrl?: string | null;
  readonly sourceUrl?: string | null;
  readonly channelName?: string | null;
  readonly channelLogo?: string | null;
  readonly language?: string | null;
  readonly publishedAt?: string | null;
  readonly isTranslated?: boolean | null;
  readonly translatedFrom?: string | null;
}

interface SavedArticlesPage {
  readonly data: SavedArticleRecord[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

export function useSavedQuery(options?: FeedQueryOptions) {
  // Backend serves saved/bookmarked articles at /bookmarks (paginated).
  // Web only needs the hash set to flag cards as saved, so we collapse the
  // first page (limit=50) into a string[] of articleHashes. Matches the
  // mobile useSavedQuery contract from mobile/src/hooks/use-saved-query.ts.
  return useQuery({
    queryKey: queryKeys.saved.list,
    queryFn: async () => {
      const result = await apiClient.get<{ data: SavedArticlesPage }>(
        '/bookmarks?limit=50',
      );
      return result.data.data.data.map((r) => r.articleHash);
    },
    enabled: options?.enabled ?? true,
  });
}

export function useSavedArticlesQuery(options?: FeedQueryOptions) {
  // Full saved-article records — used by the /saved page to render cards
  // even when the underlying article is no longer in the feed cache.
  return useQuery({
    queryKey: queryKeys.saved.articles,
    queryFn: async () => {
      const result = await apiClient.get<{ data: SavedArticlesPage }>(
        '/bookmarks?limit=50',
      );
      return result.data.data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

interface ToggleSaveInput {
  readonly articleHash: string;
  readonly save: boolean;
  /** Required by backend on save (POST). Ignored on unsave. */
  readonly article?: {
    readonly title: string;
    readonly summary?: string;
    readonly imageUrl?: string | null;
    readonly sourceUrl: string;
    readonly channelName?: string;
    readonly channelLogo?: string | null;
    readonly language?: string;
    readonly publishedAt?: string;
    readonly isTranslated?: boolean;
    readonly translatedFrom?: string | null;
  };
}

export function useToggleSaveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ToggleSaveInput) => {
      if (input.save) {
        if (!input.article) {
          throw new Error('Article body required to save (title + sourceUrl).');
        }
        await apiClient.post('/bookmarks', {
          articleHash: input.articleHash,
          ...input.article,
        });
      } else {
        await apiClient.delete(`/bookmarks/${encodeURIComponent(input.articleHash)}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.saved.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Full-text search across the feed. Mirrors mobile/src/hooks/use-feed-query.ts
// useFeedSearch — gated on >= 2 chars so we don't fire on every keystroke.
// ---------------------------------------------------------------------------

export function useFeedSearch(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: queryKeys.feed.search(trimmed),
    queryFn: async () => {
      const result = await apiClient.get<{ data: FeedPage }>(
        `/feed/search?q=${encodeURIComponent(trimmed)}&limit=20`,
      );
      return sanitizeArticles(result.data.data.data);
    },
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Tag-based feed (e.g. #housing, #visa). Backed by GET /feed/topics?tag=…
// ---------------------------------------------------------------------------

export function useTagFeedQuery(tag: string, options?: FeedQueryOptions) {
  return useInfiniteQuery({
    queryKey: queryKeys.feed.tagFeed(tag),
    queryFn: async ({ pageParam }) => {
      const params: string[] = [`tag=${encodeURIComponent(tag)}`, 'limit=20'];
      if (pageParam) params.push(`cursor=${encodeURIComponent(pageParam)}`);
      const result = await apiClient.get<{ data: FeedPage }>(
        `/feed/topics?${params.join('&')}`,
      );
      return result.data.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    enabled: (options?.enabled ?? true) && tag.trim().length > 0,
  });
}

export function useTagFeedArticles(tag: string, options?: FeedQueryOptions): FeedArticle[] {
  const { data } = useTagFeedQuery(tag, options);
  return useMemo(() => {
    if (!data?.pages) return [];
    const all = data.pages.flatMap((p) => p.data);
    const seen = new Set<string>();
    const unique = all.filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)));
    return sanitizeArticles(unique);
  }, [data]);
}

// ---------------------------------------------------------------------------
// Single-channel feed — only this channel's articles. Backs /channel/:id.
// ---------------------------------------------------------------------------

export function useChannelFeedQuery(channelId: string, options?: FeedQueryOptions) {
  return useInfiniteQuery({
    queryKey: queryKeys.feed.channelFeed(channelId),
    queryFn: async ({ pageParam }) => {
      const params: string[] = ['limit=20'];
      if (pageParam) params.push(`cursor=${encodeURIComponent(pageParam)}`);
      const result = await apiClient.get<{ data: FeedPage }>(
        `/feed/channel/${encodeURIComponent(channelId)}?${params.join('&')}`,
      );
      return result.data.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    enabled: (options?.enabled ?? true) && channelId.trim().length > 0,
  });
}

export function useChannelFeedArticles(channelId: string, options?: FeedQueryOptions): FeedArticle[] {
  const { data } = useChannelFeedQuery(channelId, options);
  return useMemo(() => {
    if (!data?.pages) return [];
    const all = data.pages.flatMap((p) => p.data);
    const seen = new Set<string>();
    const unique = all.filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)));
    return sanitizeArticles(unique);
  }, [data]);
}
